import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';

const ScoreTimeline = ({ activity }) => {
  // Transform activity data for the chart
  const data = [...activity]
    .reverse() // Oldest to newest
    .map((item) => {
      const date = new Date(item.created_at);
      const score = item.result?.overallData?.score || item.result?.overall_score || 0;
      
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
        score: score,
        type: item.exam_details?.type || item.exam_details?.interview_type || 'Session'
      };
    });

  if (data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
        <p className="text-sm font-normal text-slate-500tracking-widest">No rating history yet</p>
      </div>
    );
  }

  // Define rank zones for background coloring (Codeforces style)
  const zones = [
    { y1: 0, y2: 30, color: '#F8FAFC', label: 'Newbie' },
    { y1: 30, y2: 50, color: '#ECFDF5', label: 'Apprentice' },
    { y1: 50, y2: 70, color: '#ECFEFF', label: 'Specialist' },
    { y1: 70, y2: 85, color: '#EFF6FF', label: 'Expert' },
    { y1: 85, y2: 95, color: '#FAF5FF', label: 'Master' },
    { y1: 95, y2: 100, color: '#FEF2F2', label: 'Grandmaster' },
  ];

  return (
    <div className="w-full h-[320px] mt-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
          <XAxis 
            dataKey="date" 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#94A3B8', fontSize: 10, fontWeight: 700 }}
            dy={10}
          />
          <YAxis 
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#94A3B8', fontSize: 10, fontWeight: 700 }}
            domain={[0, 100]}
            dx={-10}
          />
          <Tooltip 
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const d = payload[0].payload;
                return (
                  <div className="bg-white p-4 border border-slate-100 shadow-xl rounded-xl">
                    <p className="text-[10px] font-black text-slate-400tracking-widest mb-1">{d.fullDate}</p>
                    <p className="text-sm font-black text-slate-900 mb-0.5">{d.type}</p>
                    <div className="flex items-center gap-2">
                        <span className="text-lg font-black text-emerald-500">{d.score}</span>
                        <span className="text-[10px] font-bold text-slate-400tracking-widest">Rating</span>
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
          
          {/* Rank Zones */}
          {zones.map((zone, idx) => (
            <ReferenceArea 
              key={idx}
              y1={zone.y1} 
              y2={zone.y2} 
              fill={zone.color} 
              fillOpacity={0.5} 
            />
          ))}

          <Line 
            type="monotone" 
            dataKey="score" 
            stroke="#0F172A" 
            strokeWidth={3} 
            dot={{ r: 4, fill: '#0F172A', stroke: '#FFFFFF', strokeWidth: 2 }}
            activeDot={{ r: 6, fill: '#10E58F', stroke: '#FFFFFF', strokeWidth: 2 }}
            animationDuration={1500}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScoreTimeline;
